/**
 * Alpha Workwear offer sheet - which offer is on the panel.
 *
 * The ledger on the right is a vertical tablist and the panels on the left are
 * its tabpanels. Every panel is already in the page, rendered by Liquid with its
 * own prices; this file only decides which one is visible. Nothing here builds
 * markup or formats money, which is the whole reason a panel price can never
 * disagree with the row price beside it.
 *
 * SELECTION FOLLOWS THE POINTER, THE FOCUS RING AND THE TIMER
 *   - hover a row            -> that offer takes the panel
 *   - focus a row, arrow keys-> same, and focus moves with it (automatic
 *                               activation, the usual tablist behaviour)
 *   - click an INACTIVE row  -> selects it, and the link is suppressed
 *   - click the ACTIVE row   -> the link is allowed through to the product,
 *                               so a phone gets "first tap shows, second opens"
 *                               and a mouse keeps the old row-opens-product
 *                               behaviour, because hovering already selected it
 *
 * THE TIMER STOPS WHENEVER MOVING WOULD BE AN INTERRUPTION
 *   - the pointer is anywhere in the band, or focus is inside it. Unlike
 *     alpha-products-carousel.js, which deliberately narrows hover-pause to the
 *     rail because pausing near the heading looked like broken autoplay, here
 *     hover IS the control. A pointer in this band means someone is reading
 *     these prices and the panel they are reading must stay put.
 *   - the tab is hidden. rAF does not fire in a background tab, so the clock
 *     cannot run up 300 advances behind the shopper's back, and the long-frame
 *     guard below throws away the one huge delta that arrives on return.
 *   - the section is off screen, via IntersectionObserver.
 *   - prefers-reduced-motion is set, in which case it never starts at all and
 *     the stylesheet drops the cross-fade too. Hover, focus and tap still work:
 *     the setting asks for no unrequested motion, not for a dead control.
 *
 * WHY A rAF CLOCK RATHER THAN setInterval
 * The active row draws a progress spine whose CSS animation is paused with the
 * timer. An interval keeps counting while a paused animation stands still, so
 * after one hover the bar and the switch drift apart and the bar becomes a lie.
 * Accumulating elapsed time only on unpaused frames keeps the two on the same
 * clock, because both stop for exactly the same milliseconds.
 */

(function () {
  const SELECTOR = '[data-alpha-offers]';
  const PREV_KEYS = ['ArrowUp', 'ArrowLeft'];
  const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];

  class AlphaOffers {
    constructor(root) {
      this.root = root;
      this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-alpha-offers-tab]'));
      this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-alpha-offers-panel]'));

      // The two surfaces a shopper actually reads. Hover-pause is bound to these
      // rather than to the whole band - see the listener block below for why.
      // Derived from the elements themselves so no extra markup hook is needed.
      this.list = this.tabs.length ? this.tabs[0].closest('ul, ol, [data-alpha-offers-list]') || this.tabs[0].parentElement : null;
      this.segs = Array.prototype.slice.call(root.querySelectorAll('[data-alpha-offers-seg]'));
      this.stage = this.panels.length ? this.panels[0].parentElement : null;

      // One offer, or a mismatch that should never happen: leave the server's
      // markup exactly as it is. The single panel is already rendered active.
      if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

      this.index = this.tabs.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      if (this.index < 0) this.index = 0;

      const seconds = parseFloat(root.dataset.autoplay || '0') || 0;
      this.interval = seconds * 1000;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hovering = false;
      this.focused = false;
      this.onScreen = true;
      this.elapsed = 0;
      this.last = null;
      this.raf = null;

      this.tick = this.tick.bind(this);

      this.bind();
      this.bindSegments();
      // Normalise from the server's state rather than assuming it: the panels
      // and the rows must agree before anything can move them.
      this.select(this.index, { focus: false, restart: false });
      this.watchScreen();
      this.startClock();
    }

    /* ------------------------------------------------------------ wiring */

    bind() {
      const self = this;

      this.tabs.forEach(function (tab, i) {
        // Hover. mouseenter rather than mouseover: it does not re-fire for every
        // child span the pointer crosses on its way across the row.
        tab.addEventListener('mouseenter', function () {
          self.select(i, { focus: false });
        });

        // Focus. Tab lands on the active row (roving tabindex), so this mostly
        // matters when something else moves focus here.
        tab.addEventListener('focus', function () {
          self.select(i, { focus: false });
        });

        tab.addEventListener('click', function (event) {
          if (i === self.index) return; // let the link open the product
          event.preventDefault();
          self.select(i, { focus: true });
        });
      });

      this.tabs.forEach(function (tab) {
        tab.addEventListener('keydown', function (event) {
          self.onKeydown(event);
        });
      });

      /*
       * Pause on hover of the LIST, not the whole band.
       *
       * Bound to the root, this pauses whenever the pointer is anywhere in a
       * full-width section - which on a normal scroll down the homepage is most
       * of the time. The merchant's report was "it pauses a lot for some reason",
       * and this was the reason: the cycle was almost never allowed to run.
       *
       * Hovering a ROW is a real signal (they are reading that offer, and hover
       * also switches to it). Hovering the hero panel counts too, since that is
       * the thing being read. The dead space around them does not.
       *
       * Focus stays bound to the root: a keyboard user tabbing through anything
       * in here should not have the content move under them.
       */
      const hoverZone = this.list || this.root;
      hoverZone.addEventListener('mouseenter', function () {
        self.hovering = true;
        self.syncPaused();
      });
      hoverZone.addEventListener('mouseleave', function () {
        self.hovering = false;
        self.syncPaused();
      });

      // The hero is the other half of the reading surface.
      if (this.stage) {
        this.stage.addEventListener('mouseenter', function () {
          self.hovering = true;
          self.syncPaused();
        });
        this.stage.addEventListener('mouseleave', function () {
          self.hovering = false;
          self.syncPaused();
        });
      }
      this.root.addEventListener('focusin', function () {
        self.focused = true;
        self.syncPaused();
      });
      this.root.addEventListener('focusout', function (event) {
        if (self.root.contains(event.relatedTarget)) return;
        self.focused = false;
        self.syncPaused();
      });

      // A hidden tab must not advance. rAF is already suspended there; this also
      // resets the clock so the panel does not jump the moment the tab returns.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          self.stopClock();
        } else {
          self.startClock();
        }
      });

      const onMotionChange = function () {
        if (self.motion.matches) self.stopClock();
        else self.startClock();
      };
      if (this.motion.addEventListener) this.motion.addEventListener('change', onMotionChange);
      else if (this.motion.addListener) this.motion.addListener(onMotionChange);
    }

    onKeydown(event) {
      let next = null;

      if (PREV_KEYS.indexOf(event.key) > -1) next = this.index - 1;
      else if (NEXT_KEYS.indexOf(event.key) > -1) next = this.index + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;

      // Wrap, so the list has no dead ends to hit.
      if (next < 0) next = this.tabs.length - 1;
      if (next > this.tabs.length - 1) next = 0;

      event.preventDefault();
      this.select(next, { focus: true });
    }

    /* --------------------------------------------------------- selection */

    select(index, options) {
      const opts = options || {};
      if (index < 0 || index > this.tabs.length - 1) return;

      const self = this;

      this.tabs.forEach(function (tab, i) {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        // Roving tabindex: one stop for the whole ledger, arrows move inside it.
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-active', active);
        if (!active) tab.classList.remove('is-timing');
      });

      this.panels.forEach(function (panel, i) {
        self.setPanel(panel, i === index);
      });

      this.index = index;
      this.syncSegments();

      if (opts.focus) this.tabs[index].focus();
      // Any deliberate switch restarts the countdown: being handed three
      // seconds on an offer you just picked is the timer arguing with you.
      if (opts.restart !== false) this.resetClock();
    }

    setPanel(panel, active) {
      panel.classList.toggle('is-active', active);

      if (active) {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('aria-hidden', 'true');
        // inert covers modern browsers completely - not focusable, not in the
        // accessibility tree, not clickable. The tabindex sweep below is the
        // floor for everything older.
        panel.setAttribute('inert', '');
      }

      // Only the elements Liquid marked. The image link inside every panel is
      // permanently tabindex="-1" by design and must not be handed a tab stop
      // by being "restored".
      panel.querySelectorAll('[data-alpha-offers-focusable]').forEach(function (el) {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    }

    /* ------------------------------------------------------------- clock */

    isPaused() {
      return this.hovering || this.focused;
    }

    /*
     * Mirror the active index onto the progress strip.
     *
     * The fill animation is restarted by removing and re-adding the class with a
     * reflow in between - assigning the same animation to an element that already
     * has it is a no-op in every browser, so without the reflow the bar would
     * only ever fill once.
     */
    syncSegments() {
      if (!this.segs || !this.segs.length) return;
      const active = this.index;
      this.segs.forEach(function (seg, i) {
        seg.classList.toggle('is-done', i < active);
        if (i === active) {
          if (!seg.classList.contains('is-active')) {
            const fill = seg.querySelector('.alpha-offers__seg-fill');
            seg.classList.add('is-active');
            if (fill) {
              fill.style.animation = 'none';
              void fill.offsetWidth;
              fill.style.animation = '';
            }
          }
        } else {
          seg.classList.remove('is-active');
        }
      });
    }

    bindSegments() {
      if (!this.segs || !this.segs.length) return;
      const self = this;
      this.segs.forEach(function (seg, i) {
        seg.addEventListener('click', function () {
          self.select(i, { restart: true });
        });
      });
    }

    syncPaused() {
      const paused = this.isPaused();

      // The stylesheet freezes the progress spine on this class, so the bar and
      // the clock stop on the same frame.
      this.root.classList.toggle('is-paused', paused);
    }

    watchScreen() {
      const self = this;
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.onScreen = entry.isIntersecting;
          });
          if (self.onScreen) self.startClock();
          else self.stopClock();
        },
        { threshold: 0 }
      );
      this.observer.observe(this.root);
    }

    startClock() {
      if (!this.interval) return; // autoplay off, or a single offer
      if (this.motion.matches) return; // never move unasked
      if (document.hidden || !this.onScreen) return;
      if (this.raf) return;

      this.last = null;
      this.elapsed = 0;
      this.markTiming(true);
      this.raf = window.requestAnimationFrame(this.tick);
    }

    stopClock() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = null;
      this.last = null;
      this.markTiming(false);
    }

    resetClock() {
      if (!this.raf) return;
      this.elapsed = 0;
      this.last = null;
      this.markTiming(true);
    }

    // Restarting the spine animation means taking the class off, forcing a
    // reflow and putting it back; without the reflow the browser coalesces the
    // two changes and the animation never starts over.
    markTiming(on) {
      const tab = this.tabs[this.index];
      if (!tab) return;
      tab.classList.remove('is-timing');
      if (!on) return;
      void tab.offsetWidth;
      tab.classList.add('is-timing');
    }

    tick(now) {
      // The editor swaps the whole section for a fresh node; the old instance
      // must not keep animating a detached one.
      if (!this.root.isConnected) {
        this.stopClock();
        return;
      }

      this.raf = window.requestAnimationFrame(this.tick);

      if (this.last === null) {
        this.last = now;
        return;
      }

      const delta = now - this.last;
      this.last = now;

      if (this.isPaused()) return; // clock stands still, it does not catch up
      // A frame gap this long is a throttled or backgrounded tab, not elapsed
      // reading time. Crediting it would advance several offers at once the
      // moment the tab comes back.
      if (delta > 1000) return;

      this.elapsed += delta;
      if (this.elapsed < this.interval) return;

      this.elapsed = 0;
      let next = this.index + 1;
      if (next > this.tabs.length - 1) next = 0;
      this.select(next, { focus: false, restart: false });
      this.markTiming(true);
    }
  }

  const init = function (root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      if (el.dataset.alphaOffersReady) return;
      el.dataset.alphaOffersReady = 'true';
      new AlphaOffers(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor re-renders a section into a fresh DOM node.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
